package com.dotdrives.controller

import com.dotdrives.core.DotController
import com.dotdrives.csv.CsvExistingGiver
import com.dotdrives.model.GiverSummary
import com.dotdrives.model.NewGiver
import com.dotdrives.repository.GiverRepository
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlin.math.ceil

data class GiverFilters(
    val stewardUserId: String? = null,
    val giverType: String? = null,
    val tag: String? = null,
)

data class GiversUiState(
    val loading: Boolean = false,
    val items: List<GiverSummary> = emptyList(),
    val query: String = "",
    val filters: GiverFilters = GiverFilters(),
    val page: Int = 1,
    val totalCount: Long = 0,
    val error: String? = null,
) {
    val totalPages: Int
        get() = lastPageFor(totalCount)
}

class GiversController(
    private val repo: GiverRepository,
    scope: CoroutineScope,
    private val orgId: String,
) : DotController<GiversUiState>(GiversUiState(), scope) {
    private var searchJob: Job? = null
    private var loadGeneration = 0L

    suspend fun listForCsvImport(): Result<List<CsvExistingGiver>> =
        repo.listForCsvImport(orgId)

    suspend fun createMany(givers: List<NewGiver>): Result<Int> =
        repo.createMany(orgId, givers)

    fun load() {
        searchJob?.cancel()
        val generation = nextGeneration()
        loadPage(state.value.page, generation)
    }

    fun search(query: String) {
        searchJob?.cancel()
        val generation = nextGeneration()
        update { it.copy(query = query, page = 1, error = null) }
        searchJob = scope.launch {
            delay(SEARCH_DEBOUNCE_MILLIS)
            loadPage(page = 1, generation = generation)
        }
    }

    fun setFilter(filters: GiverFilters) {
        searchJob?.cancel()
        val generation = nextGeneration()
        update { it.copy(filters = filters, page = 1, error = null) }
        loadPage(page = 1, generation = generation)
    }

    fun goToPage(n: Int) {
        val page = n.coerceIn(1, state.value.totalPages)
        if (page == state.value.page && state.value.items.isNotEmpty()) return
        searchJob?.cancel()
        val generation = nextGeneration()
        update { it.copy(page = page, error = null) }
        loadPage(page, generation)
    }

    private fun loadPage(
        page: Int,
        generation: Long,
        allowPageClamp: Boolean = true,
    ) {
        val snapshot = state.value
        update { current ->
            if (isCurrent(generation)) current.copy(loading = true, error = null) else current
        }
        scope.launch {
            try {
                repo.list(
                    orgId = orgId,
                    query = snapshot.query.trim().takeIf(String::isNotEmpty),
                    stewardUserId = snapshot.filters.stewardUserId,
                    giverType = snapshot.filters.giverType,
                    tag = snapshot.filters.tag,
                    page = page,
                    pageSize = GIVERS_PAGE_SIZE,
                ).onSuccess { result ->
                    if (!isCurrent(generation)) return@onSuccess
                    val lastPage = lastPageFor(result.totalCount)
                    if (
                        allowPageClamp &&
                        result.totalCount > 0 &&
                        page > lastPage
                    ) {
                        update { current ->
                            if (isCurrent(generation)) {
                                current.copy(page = lastPage)
                            } else {
                                current
                            }
                        }
                        loadPage(
                            page = lastPage,
                            generation = generation,
                            allowPageClamp = false,
                        )
                        return@onSuccess
                    }
                    update { current ->
                        if (isCurrent(generation)) {
                            current.copy(
                                loading = false,
                                items = result.items,
                                page = result.page,
                                totalCount = result.totalCount,
                                error = null,
                            )
                        } else {
                            current
                        }
                    }
                }.onFailure { publishError(it, generation) }
            } catch (error: Throwable) {
                publishError(error, generation)
            }
        }
    }

    private fun publishError(error: Throwable, generation: Long) {
        if (error is CancellationException) throw error
        if (!isCurrent(generation)) return
        update { current ->
            if (isCurrent(generation)) {
                current.copy(
                    loading = false,
                    error = error.message ?: "Something went wrong",
                )
            } else {
                current
            }
        }
    }

    private fun nextGeneration(): Long {
        loadGeneration += 1
        return loadGeneration
    }

    private fun isCurrent(generation: Long): Boolean =
        generation == loadGeneration
}

private const val SEARCH_DEBOUNCE_MILLIS = 250L
private const val GIVERS_PAGE_SIZE = 20

private fun lastPageFor(totalCount: Long): Int =
    maxOf(1, ceil(totalCount.toDouble() / GIVERS_PAGE_SIZE).toInt())
